import { useEffect, useMemo, useRef, useState } from "react";
import { SourceCitation } from "../../api/documentAgent";
import { matchesForPage } from "./highlighting";

// Renders a plain-text document (.txt/.md/.csv/.tsv/.json/.xml) with the
// cited lines highlighted. The whole file is rendered as REACT TEXT NODES —
// never innerHTML — so document content stays inert regardless of what
// markup it happens to contain. Matching is line-based via the shared fuzzy
// engine; matched lines are wrapped in <mark> elements.

type TextState =
    | { status: "loading" }
    | { status: "error" }
    | { status: "ready"; lines: string[] };

type TextPreviewProps = {
    blob: Blob;
    citations: SourceCitation[];
    passageJump: { citationIndex: number; nonce: number } | null;
};

// jsdom (jest) lacks Blob.prototype.text in some versions; FileReader is the
// universally available fallback and behaves identically for UTF-8 text.
function readBlobAsText(blob: Blob): Promise<string> {
    if (typeof blob.text === "function") return blob.text();
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(typeof reader.result === "string" ? reader.result : "");
        reader.onerror = () => reject(reader.error ?? new Error("Could not read the file."));
        reader.readAsText(blob);
    });
}

function scrollLineIntoView(element: HTMLElement | null | undefined) {
    // scrollIntoView is missing under jsdom and some embedded webviews;
    // skipping the scroll is strictly cosmetic.
    if (element && typeof element.scrollIntoView === "function") {
        try {
            element.scrollIntoView({ block: "center" });
        } catch {
            // Older engines reject the options object; the fallback call
            // below still lands the line in view.
            element.scrollIntoView();
        }
    }
}

export default function TextPreview({ blob, citations, passageJump }: TextPreviewProps) {
    const [state, setState] = useState<TextState>({ status: "loading" });
    const lineRefs = useRef(new Map<number, HTMLElement>());
    const hasAutoScrolledRef = useRef(false);

    useEffect(() => {
        let cancelled = false;
        setState({ status: "loading" });
        hasAutoScrolledRef.current = false;
        readBlobAsText(blob)
            .then((text) => {
                if (!cancelled) setState({ status: "ready", lines: text.split(/\r\n|\r|\n/) });
            })
            .catch(() => {
                if (!cancelled) setState({ status: "error" });
            });
        return () => {
            cancelled = true;
        };
    }, [blob]);

    const analysis = useMemo(() => {
        if (state.status !== "ready") return null;
        const quotes = citations.map((citation) => citation.quote);
        const matchedIndices = matchesForPage(state.lines, quotes);
        // First matched line per citation, for passage-list jumps.
        const firstMatchByCitation = citations.map((citation) => {
            const matches = matchesForPage(state.lines, [citation.quote]);
            return matches.length > 0 ? matches[0] : null;
        });
        return {
            matched: new Set(matchedIndices),
            firstMatch: matchedIndices.length > 0 ? matchedIndices[0] : null,
            firstMatchByCitation,
        };
    }, [state, citations]);

    // Bring the first highlighted line into view once per loaded document.
    useEffect(() => {
        if (!analysis || analysis.firstMatch == null || hasAutoScrolledRef.current) return;
        hasAutoScrolledRef.current = true;
        scrollLineIntoView(lineRefs.current.get(analysis.firstMatch));
    }, [analysis]);

    // Passage-list clicks scroll to that passage's first matched line.
    useEffect(() => {
        if (!passageJump || !analysis) return;
        const target = analysis.firstMatchByCitation[passageJump.citationIndex];
        if (target != null) scrollLineIntoView(lineRefs.current.get(target));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [passageJump]);

    if (state.status === "loading") {
        return <p className="py-6 text-center text-sm text-gray-400">Loading document…</p>;
    }

    if (state.status === "error") {
        return (
            <div className="rounded-md border border-red-900/60 bg-red-950/20 px-4 py-3 text-sm text-red-300">
                This document could not be read as text.
            </div>
        );
    }

    const matched = analysis?.matched ?? new Set<number>();

    return (
        <div className="h-full min-h-0 overflow-y-auto rounded-md border border-primary_one-100 bg-primary_one-150 px-4 py-3">
            {analysis != null && analysis.firstMatch == null && (
                <div role="status" className="mb-3 rounded-md border border-primary_one-100 bg-primary_one-140 px-3 py-2 text-xs text-gray-300">
                    The cited passages could not be located verbatim — the wording may differ from the original file.
                </div>
            )}
            <div className="whitespace-pre-wrap break-words text-sm leading-6 text-gray-200">
                {state.lines.map((line, index) => (
                    <div
                        key={index}
                        ref={matched.has(index)
                            ? (element) => {
                                if (element) lineRefs.current.set(index, element);
                                else lineRefs.current.delete(index);
                            }
                            : undefined}
                    >
                        {matched.has(index) ? (
                            <mark className="rounded-sm bg-primary_one-200/30 text-inherit">{line}</mark>
                        ) : (
                            line.length > 0 ? line : " "
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
}
